/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { PropsWithChildren, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import ExtensionOutlined from '@material-ui/icons/ExtensionOutlined';
import PeopleOutlined from '@material-ui/icons/PeopleOutlined';
import HomeOutlined from '@material-ui/icons/HomeOutlined';
import LayersOutlined from '@material-ui/icons/LayersOutlined';
import LibraryBooksOutlined from '@material-ui/icons/LibraryBooksOutlined';
import MenuIcon from '@material-ui/icons/Menu';
import MonetizationOnOutlined from '@material-ui/icons/MonetizationOnOutlined';
import SearchIcon from '@material-ui/icons/Search';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import './sidebarStyles.css';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { CustomSidebarSearchModal, CustomTechDocsSearchResultListItem } from '@internal/plugin-custom-search-ui';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  useSidebarPinState,
  Link,
} from '@backstage/core-components';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import Tooltip from '@material-ui/core/Tooltip';
import AndroidIcon from '@material-ui/icons/Android';
import { DfpSidebarNav } from './DfpSidebarNav';
import { AiPlatformSidebarNav } from './AiPlatformSidebarNav';

const LOGO_HEIGHT = 32;

// ── Backstage sidebar width is set via sidebarOptions prop (see <Sidebar>) ─
// The Sidebar component is NOT a MuiDrawer — it renders as a plain <nav>
// element. Width must be passed through the SidebarConfigContext, not via CSS.

// ── Logo ──────────────────────────────────────────────────────────────────

const useSidebarLogoStyles = makeStyles({
  root: {
    // Fill the full sidebar width so the pin button can sit at the far right
    width: '100%',
    height: 3 * LOGO_HEIGHT,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
    paddingRight: 4,
    boxSizing: 'border-box',
  },
  link: {
    flex: '1 1 auto',
    marginLeft: 24,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  pinToggle: {
    flexShrink: 0,
    background: 'none',
    border: 'none',
    color: 'rgba(180, 180, 180, 0.7)',
    cursor: 'pointer',
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-1px',
    padding: '6px 8px',
    borderRadius: 4,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  pinEmoji: {
    fontSize: 15,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const { isPinned, toggleSidebarPinState } = useSidebarPinState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
      {isOpen && (
        <button
          onClick={toggleSidebarPinState}
          className={classes.pinToggle}
          title={isPinned ? 'Collapse sidebar' : 'Pin sidebar'}
          aria-label={isPinned ? 'Collapse sidebar' : 'Pin sidebar'}
        >
          {isPinned ? '«' : <span className={classes.pinEmoji}>📌</span>}
        </button>
      )}
    </div>
  );
};

// ── ThemeWatcher ───────────────────────────────────────────────────────────
// Backstage's AppThemeProvider does NOT write any attribute to <html>, so
// html[data-theme] CSS selectors would never match. This component reads the
// active theme from appThemeApiRef and keeps html[data-theme] in sync so our
// sidebarStyles.css rules fire correctly for both light and dark themes.

const ThemeWatcher = () => {
  const themeApi = useApi(appThemeApiRef);

  useEffect(() => {
    const applyVariant = (themeId: string | undefined) => {
      const themes = themeApi.getInstalledThemes();
      const active = themes.find(t => t.id === themeId);

      // If theme is found in our custom themes, use its variant
      if (active) {
        document.documentElement.dataset.theme = active.variant ?? 'dark';
        return;
      }

      // If theme not found (e.g., "auto" theme), detect from OS preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.dataset.theme = prefersDark ? 'dark' : 'light';
    };

    applyVariant(themeApi.getActiveThemeId());
    const sub = themeApi.activeThemeId$().subscribe(applyVariant);

    // Listen for OS theme changes when in auto mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleOsThemeChange = () => {
      applyVariant(themeApi.getActiveThemeId());
    };
    mediaQuery.addEventListener('change', handleOsThemeChange);

    return () => {
      sub.unsubscribe();
      mediaQuery.removeEventListener('change', handleOsThemeChange);
    };
  }, [themeApi]);

  return null;
};

// ── Root ──────────────────────────────────────────────────────────────────

export const Root = ({ children }: PropsWithChildren<{}>) => {
  return (
    <SidebarPage>
      <ThemeWatcher />
      <Sidebar sidebarOptions={{ drawerWidthOpen: 240 }}>
          <SidebarLogo />

          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <CustomSidebarSearchModal
              resultItemComponents={<CustomTechDocsSearchResultListItem />}
            />
          </SidebarGroup>

          <SidebarDivider />

          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarItem icon={HomeOutlined} to="catalog" text="Home" {...{ end: true }} />
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={PeopleOutlined}
            />
            <SidebarItem icon={ExtensionOutlined} to="api-docs" text="APIs" />
            <SidebarItem icon={LibraryBooksOutlined} to="docs" text="Docs" />
            <SidebarItem
              icon={TrackChangesIcon}
              to="tech-radar"
              text="Tech Radar"
            />
            <DfpSidebarNav />
            <Tooltip title="AI Dev Tool Portal" placement="bottom-end">
              <SidebarItem icon={AndroidIcon} to="ai-dev-tools" text="AI Dev Tools" />
            </Tooltip>
            <AiPlatformSidebarNav />
            <SidebarItem icon={LayersOutlined} to="explore" text="Explore" />
            <SidebarItem icon={AddCircleOutline} to="create" text="Create..." />
            <SidebarDivider />
            <SidebarItem
              icon={MonetizationOnOutlined}
              to="cost-insights"
              text="Cost Insights"
            />
          </SidebarGroup>

          <SidebarSpace />
          <SidebarDivider />

          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
